import React, { useEffect, useRef } from "react";
import { Text } from "components-lib";
import SmartAutocompleteInput from "../SmartAutocompleteInput";
import { saveToStorage, getStoredSuggestions } from "../utils/storageUtils";

/**
 * AllergiesSection Component
 * Manages patient allergy information including medicines, food, and other allergies
 * Provides enhanced autocomplete functionality with API integration and local storage
 */
const AllergiesSection = ({
  medicines,
  setMedicines,
  food,
  setFood,
  other,
  setOther,
  newMedicine,
  setNewMedicine,
  newFood,
  setNewFood,
  newOther,
  setNewOther,
  appointmentId,
  doctorRecommendations,
  PastpatientSpecifcData,
  appointmentPrescription,
}) => {

    const hydratedFromPrescriptionRef = useRef(null);


    useEffect(() => {
      console.log(
        "🔴 PastpatientSpecifcData Recived at Allergies",
        JSON.stringify(PastpatientSpecifcData)
      );
    }, [PastpatientSpecifcData]);

  // LocalStorage keys for autocomplete data
  const STORAGE_KEYS = {
    ALLERGY_MEDICINES: "autocomplete_allergy_medicines",
    ALLERGY_FOOD: "autocomplete_allergy_food",
    ALLERGY_OTHER: "autocomplete_allergy_other",
  };

  // Handle allergy medicine selection
  const handleAllergyMedicineSelect = (value, metadata) => {
    setNewMedicine(value);
    if (metadata?.isCustomInput || metadata?.isFromSuggestions) {
      saveToStorage(STORAGE_KEYS.ALLERGY_MEDICINES, value);
    }
  };

  // Handle allergy food selection
  const handleAllergyFoodSelect = (value, metadata) => {
    setNewFood(value);
    if (metadata?.isCustomInput || metadata?.isFromSuggestions) {
      saveToStorage(STORAGE_KEYS.ALLERGY_FOOD, value);
    }
  };

  // Handle allergy other selection
  const handleAllergyOtherSelect = (value, metadata) => {
    setNewOther(value);
    if (metadata?.isCustomInput || metadata?.isFromSuggestions) {
      saveToStorage(STORAGE_KEYS.ALLERGY_OTHER, value);
    }
  };

  // Handle input changes for autocomplete
  const handleAllergyMedicineChange = (e) => {
    setNewMedicine(e.target.value);
  };

  const handleAllergyFoodChange = (e) => {
    setNewFood(e.target.value);
  };

  const handleAllergyOtherChange = (e) => {
    setNewOther(e.target.value);
  };

  // Handle delete item functionality
  const handleDeleteItem = (type, index) => {
    switch (type) {
      case "medicines":
        setMedicines(medicines.filter((_, i) => i !== index));
        break;
      case "food":
        setFood(food.filter((_, i) => i !== index));
        break;
      case "other":
        setOther(other.filter((_, i) => i !== index));
        break;
    }
  };

  // Handle add item functionality
  const handleAddItem = (type) => {
    switch (type) {
      case "medicines":
        if (newMedicine.trim()) {
          setMedicines([...medicines, newMedicine]);
          saveToStorage(STORAGE_KEYS.ALLERGY_MEDICINES, newMedicine);
          setNewMedicine("");
        }
        break;
      case "food":
        if (newFood.trim()) {
          setFood([...food, newFood]);
          saveToStorage(STORAGE_KEYS.ALLERGY_FOOD, newFood);
          setNewFood("");
        }
        break;
      case "other":
        if (newOther.trim()) {
          setOther([...other, newOther]);
          saveToStorage(STORAGE_KEYS.ALLERGY_OTHER, newOther);
          setNewOther("");
        }
        break;
    }
  };


  // Process allergies from various sources (API response or PastpatientSpecifcData)
  const processAllergySource = (source) => {
    if (!source) return;

    // Normalized config for lists
    const listConfig = {
      medicines: {
        setter: setMedicines,
        storageKey: STORAGE_KEYS.ALLERGY_MEDICINES,
        clearNew: () => setNewMedicine(""),
      },
      food: {
        setter: setFood,
        storageKey: STORAGE_KEYS.ALLERGY_FOOD,
        clearNew: () => setNewFood(""),
      },
      others: {
        setter: setOther,
        storageKey: STORAGE_KEYS.ALLERGY_OTHER,
        clearNew: () => setNewOther(""),
      },
    };

    const addIfNotExists = (val, listName) => {
      if (!val) return;
      const cfg = listConfig[listName];
      if (!cfg) return;
      const normVal = String(val).trim();
      if (!normVal) return;

      // use functional update to avoid stale state and ensure uniqueness (case-insensitive)
      cfg.setter((prev = []) => {
        const exists = prev.some(
          (i) =>
            String(i || "")
              .trim()
              .toLowerCase() === normVal.toLowerCase()
        );
        if (exists) return prev;
        const next = [...prev, normVal];
        // persist the newly added value
        try {
          saveToStorage(cfg.storageKey, normVal);
        } catch (e) {
          console.log("Storage save error:", e);
        }
        return next;
      });
      // clearing the input field (ok even if value was duplicate)
      cfg.clearNew();
    };

    const processField = (fieldValue, listName) => {
      if (fieldValue == null) return;

      // arrays
      if (Array.isArray(fieldValue)) {
        fieldValue.forEach((it) => {
          const v = String(it || "").trim();
          if (v) addIfNotExists(v, listName);
        });
        return;
      }

      // string: possibly comma separated
      const str = String(fieldValue).trim();
      if (!str) return;
      if (str.includes(",")) {
        str
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((s) => addIfNotExists(s, listName));
      } else {
        addIfNotExists(str, listName);
      }
    };

    const processAllergyObject = (allergy) => {
      if (!allergy) return;
      // support different key names (others / other)
      processField(allergy.medicines, "medicines");
      processField(allergy.food, "food");
      processField(allergy.others ?? allergy.other, "others");

      console.log(`Allergy Details for appId ${appointmentId}:`);
      console.log(`Medicines: ${JSON.stringify(allergy.medicines)}`);
      console.log(`Food: ${JSON.stringify(allergy.food)}`);
      console.log(`Others: ${JSON.stringify(allergy.others ?? allergy.other)}`);
      console.log("------------------------");
    };

    // Normalize possible shapes:
    // - array of allergy objects
    // - object with .allergies array
    // - object with .data.allergies (API wrapper)
    // - single allergy-like object
    const resolvedArray =
      Array.isArray(source)
        ? source
        : Array.isArray(source.allergies)
        ? source.allergies
        : Array.isArray(source.data?.allergies)
        ? source.data.allergies
        : null;

    if (resolvedArray) {
      if (resolvedArray.length === 0) {
        console.log("No allergies found in provided array source.");
        return;
      }
      resolvedArray.forEach(processAllergyObject);
      return;
    }

    // If source itself looks like wrapper containing .data which directly has medicines/food/others
    if (source.data && (source.data.medicines || source.data.food || source.data.others || source.data.other)) {
      processAllergyObject(source.data);
      return;
    }

    // Fallback: single object that may contain medicines/food/others directly
    processAllergyObject(source);
  };

  // When PastpatientSpecifcData prop is available/changes, merge into UI lists
  useEffect(() => {
    if (!PastpatientSpecifcData) return;
    try {
      processAllergySource(PastpatientSpecifcData);
    } catch (e) {
      console.log("Error processing PastpatientSpecifcData:", e);
    }
  }, [PastpatientSpecifcData]);

  useEffect(() => {
    if (!appointmentPrescription || !appointmentId) return;
    if (hydratedFromPrescriptionRef.current === appointmentId) return;

    try {
      // Prefer the normalized data payload passed from the parent hook
      const source = appointmentPrescription.data || appointmentPrescription;
      processAllergySource(source?.allergies || source?.prescription?.allergies || source);
      hydratedFromPrescriptionRef.current = appointmentId;
    } catch (error) {
      console.error(
        `Error hydrating allergies from appointment prescription ${appointmentId}:`,
        error,
      );
    }
  }, [appointmentPrescription, appointmentId]);

  // Render allergy items with delete functionality
  const renderAllergyItems = (items, type) => {
    return items.map((item, index) => (
      <div
        key={index}
        style={{
          display: "inline-block",
          backgroundColor: "#f0f0f0",
          borderRadius: "15px",
          padding: "10px 15px",
          margin: "5px",
          position: "relative",
        }}
      >
        {item}
        <button
          style={{
            backgroundColor: "#f0f0f0",
            color: "red",
            border: "none",
            borderRadius: "40%",
            cursor: "pointer",
            padding: "2px 6px",
            fontSize: "15px",
            marginLeft: "10px",
            fontWeight: "bold",
          }}
          onClick={() => handleDeleteItem(type, index)}
        >
          X
        </button>
      </div>
    ));
  };

  return (
    <tr>
      <td
        style={{
          border: "1px solid #E2E8F0",
          padding: "10px",
          background: "white",
          textAlign: "left",
        }}
      >
        <Text fontWeight="bold" style={{ fontSize: "16px" }}>
          Allergies:
        </Text>
      </td>
      <td
        style={{
          border: "1px solid #E2E8F0",
          padding: "10px",
          background: "white",
          textAlign: "left",
        }}
      >
        <table width="100%" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th
                style={{
                  border: "1px solid #E2E8F0",
                  padding: "10px",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                Medicines
              </th>
              <th
                style={{
                  border: "1px solid #E2E8F0",
                  padding: "10px",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                Food
              </th>
              <th
                style={{
                  border: "1px solid #E2E8F0",
                  padding: "10px",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                Other
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              {/* Medicines Column */}
              <td
                style={{
                  border: "none",
                  padding: "12px",
                  background: "white",
                  textAlign: "left",
                  boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
                  borderRadius: "12px",
                }}
              >
                {renderAllergyItems(medicines, "medicines")}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginTop: "8px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <SmartAutocompleteInput
                      value={newMedicine || ""}
                      onChange={handleAllergyMedicineChange}
                      onSelect={handleAllergyMedicineSelect}
                      placeholder="Enter medicine allergy..."
                      category="allergies"
                      subcategory="medicines"
                      allowCustomInput={true}
                      size="sm"
                      style={{
                        width: "100%",
                        padding: "5px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                      }}
                    />
                  </div>
                  <button
                    style={{
                      backgroundColor: "#4CAF50",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                      padding: "6px 12px",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => handleAddItem("medicines")}
                  >
                    Add
                  </button>
                </div>
              </td>

              {/* Food Column */}
              <td
                style={{
                  border: "none",
                  padding: "12px",
                  background: "white",
                  textAlign: "left",
                  boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
                  borderRadius: "12px",
                }}
              >
                {renderAllergyItems(food, "food")}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginTop: "8px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <SmartAutocompleteInput
                      value={newFood}
                      onChange={handleAllergyFoodChange}
                      onSelect={handleAllergyFoodSelect}
                      placeholder="Enter food allergy..."
                      category="allergies"
                      subcategory="food"
                      allowCustomInput={true}
                      size="sm"
                      style={{
                        width: "100%",
                        padding: "5px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                      }}
                    />
                  </div>
                  <button
                    style={{
                      backgroundColor: "#4CAF50",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                      padding: "6px 12px",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => handleAddItem("food")}
                  >
                    Add
                  </button>
                </div>
              </td>

              {/* Other Column */}
              <td
                style={{
                  border: "none",
                  padding: "12px",
                  background: "white",
                  textAlign: "left",
                  boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
                  borderRadius: "12px",
                }}
              >
                {renderAllergyItems(other, "other")}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginTop: "8px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <SmartAutocompleteInput
                      value={newOther}
                      onChange={handleAllergyOtherChange}
                      onSelect={handleAllergyOtherSelect}
                      placeholder="Enter other allergy..."
                      category="allergies"
                      subcategory="others"
                      allowCustomInput={true}
                      size="sm"
                      style={{
                        width: "100%",
                        padding: "5px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                      }}
                    />
                  </div>
                  <button
                    style={{
                      backgroundColor: "#4CAF50",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                      padding: "6px 12px",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => handleAddItem("other")}
                  >
                    Add
                  </button>
                </div>
              </td>
            </tr>
            {/* <button
              style={{
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                padding: "5px 10px",
                fontSize: "12px",
              }}
              onClick={() => makeRequest()}
            >
              Make Request
            </button> */}
          </tbody>
        </table>
      </td>
    </tr>
  );
};

export default AllergiesSection;
